import React, { useEffect } from 'react';
import {
  Text,
  Icon,
  Form,
  TextFieldControl,
  Link,
  Stack,
  withTranslation,
  Trans,
  Information,
  Tooltip,
  Button,
} from '@piwikpro/ui-components';
import { FormikActions } from 'formik';
import * as Yup from 'yup';
import { TFunction } from 'react-i18next';
import { withAccess, withInject } from '@piwikpro/platform';
import { connect } from 'react-redux';

import {
  CookieInformationService,
  cookieInformationAccountCreateFormShown,
} from '../../CookieInformationService';

interface FormValues {
  accountName: string
  generalError?: string
}

interface InjectedProps {
  cookieInformation: CookieInformationService
  accessManager: any
  t: TFunction
}

interface Props extends InjectedProps {
  t: TFunction
  onDirtyChange?: (dirty: boolean) => void
}

interface FormContentProps {
  errors: { generalError?: string }
  submitForm: () => void
  isValid: boolean
  isSubmitting: boolean
  dirty: boolean
  onDirtyChange?: (dirty: boolean) => void
  t: TFunction
  isSubmitAllowedByRole: boolean
  showCreateForm: () => void
}

const FormContent: React.FC<FormContentProps> = ({
  errors,
  submitForm,
  isValid,
  isSubmitting,
  dirty,
  onDirtyChange,
  t,
  isSubmitAllowedByRole,
  showCreateForm,
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]);

  return (
    <Stack vertical spacing="wide">
      <Stack.Item>
        <Text as="p">
          {t('cookieInformation.connectAccount.description')}
        </Text>
      </Stack.Item>
      <Stack.Item previousSpacing="narrow">
        <Stack spacing="extraNarrow" verticalAlignment="center">
          <Stack.Item>
            <Icon size="small" color="blue" name="info-circle-outline" />
          </Stack.Item>
          <Stack.Item>
            <Text as="p" size={12} color="InkLighter" dataId="Caption">
              {t('cookieInformation.connectAccount.infoText')}
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      {errors.generalError && (
        <Stack.Item fill>
          <Information type="error">{errors.generalError as string}</Information>
        </Stack.Item>
      )}
      <Stack.Item previousSpacing={errors.generalError ? 'default' : 'wide'} fill>
        <TextFieldControl
          label={t('cookieInformation.connectAccount.accountNameLabel')}
          name="accountName"
          required
          info={t('cookieInformation.connectAccount.accountNameInfo')}
        />
      </Stack.Item>
      <Stack.Item fill>
        <Tooltip
          content={!isSubmitAllowedByRole && t('accessControl.permissions.role.owner.required.info')}
        >
          <Button
            appearance="primary"
            text={t('cookieInformation.connectAccount.addButton')}
            fullWidth
            onClick={submitForm}
            disabled={!isSubmitAllowedByRole || !isValid || isSubmitting}
          />
        </Tooltip>
      </Stack.Item>
      <Stack.Item fill previousSpacing="default">
        <Text as="p" align="center">
          <Trans
            i18nKey="cookieInformation.connectAccount.dontHaveAccount"
            components={{
              signupLink: <Link onClick={showCreateForm}>{t('cookieInformation.connectAccount.signupLink')}</Link>,
            }}
          />
        </Text>
      </Stack.Item>
    </Stack>
  );
};

const ConnectedFormContent = connect(null, {
  showCreateForm: cookieInformationAccountCreateFormShown,
})(FormContent);

export const CookieInformationConnectAccount: React.FC<Props> = ({
  cookieInformation,
  accessManager,
  t,
  onDirtyChange,
}) => {
  const isSubmitAllowedByRole = accessManager.isGranted({ role: 'OWNER' });
  const handleSubmit = async (values: { accountName: string }, { setSubmitting, setErrors }: FormikActions<FormValues>) => {
    try {
      await cookieInformation.connectAccount({
        accountName: values.accountName,
      });
    } catch (error: any) {
      if (error.errors && error.errors.detail) {
        setErrors({ generalError: error.errors.detail });
      } else {
        setErrors({
          accountName: error.errors.account_name.pop(),
        });
      }
    }

    setSubmitting(false);
  };

  const validationSchema = () =>
    Yup.object().shape({
      accountName: Yup.string().required(t('cookieInformation.connectAccount.validation.accountNameRequired')),
    });

  return (
    <Form
      id="cookie-information-connect-account-form"
      initialValues={{ accountName: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      render={({
        errors, submitForm, isValid, isSubmitting, dirty,
      }) => (
        <ConnectedFormContent
          errors={errors}
          submitForm={submitForm}
          isValid={isValid}
          isSubmitting={isSubmitting}
          dirty={dirty}
          onDirtyChange={onDirtyChange}
          t={t}
          isSubmitAllowedByRole={isSubmitAllowedByRole}
        />
      )}
    />
  );
};

export default withInject<InjectedProps>({
  cookieInformation: 'CookieInformationCrate.CookieInformationService',
})(withTranslation(['administration'])(withAccess(CookieInformationConnectAccount)));
